import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useListModelsQuery } from "store/queries/chat";
import styled from "styled-components";
import { IOpenAiChatModel } from "types/Chat";

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const AccountTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const ModelName = styled.span`
  color: #0066cc;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

function ModelsTable() {
  const navigate = useNavigate();
  const { data: models, isLoading, refetch } = useListModelsQuery();

  const parsedModels = useMemo(() => {
    if (!models) return [];
    return models
      .map((model: IOpenAiChatModel) => ({
        id: model.id,
        name: model.name,
        type: model.type,
        accountCount: model.accounts?.length || 0,
        accounts: model.accounts || [],
        createdAt: new Date(model.createdAt).toLocaleDateString(),
        createdAtDate: new Date(model.createdAt),
        fullModel: model,
      }))
      .sort((a, b) => b.createdAtDate.getTime() - a.createdAtDate.getTime());
  }, [models]);

  const modelActions = useCallback(
    (rowData: any) => {
      const handleCopy = () => {
        navigate(`/chatbot/models/new`, {
          state: { copyFromModel: rowData.fullModel },
        });
      };

      return (
        <Row>
          <Button
            className="p-button-link"
            onClick={() => navigate(`/chatbot/models/edit/${rowData.id}`)}
          >
            <p className="small-text">Edit</p>
          </Button>
          <Button className="p-button-link" onClick={handleCopy}>
            <p className="small-text">Copy</p>
          </Button>
        </Row>
      );
    },
    [navigate]
  );

  const nameBody = useCallback(
    (rowData: any) => {
      return (
        <ModelName onClick={() => navigate(`/chatbot/models/${rowData.id}`)}>
          {rowData.name}
        </ModelName>
      );
    },
    [navigate]
  );

  const typeBody = useCallback((rowData: any) => {
    const typeLabels = {
      PatientChatBot: "Patient Chat Bot",
      ContractManagementBot: "Hospital Staff Bot",
    };
    return (
      <Tag
        value={
          typeLabels[rowData.type as keyof typeof typeLabels] || rowData.type
        }
        severity={rowData.type === "PatientChatBot" ? "success" : "info"}
      />
    );
  }, []);

  const accountsBody = useCallback((rowData: any) => {
    if (!rowData.accounts || rowData.accounts.length === 0) {
      return <span className="text-muted">No accounts</span>;
    }
    return (
      <AccountTags>
        {rowData.accounts.slice(0, 3).map((account: any) => (
          <Tag key={account.id} value={account.name} />
        ))}
        {rowData.accounts.length > 3 && (
          <Tag value={`+${rowData.accounts.length - 3} more`} />
        )}
      </AccountTags>
    );
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <DataTable
      rows={10}
      paginator
      stripedRows
      className="p-fluid"
      value={parsedModels}
      responsiveLayout="scroll"
      style={{ cursor: "pointer" }}
      emptyMessage="No models to show."
    >
      <Column
        field="name"
        style={{ minWidth: "200px" }}
        header="Model Name"
        body={nameBody}
      />
      <Column
        field="type"
        style={{ minWidth: "200px" }}
        header="Type"
        body={typeBody}
      />
      <Column
        field="accounts"
        style={{ minWidth: "250px" }}
        header="Associated Accounts"
        body={accountsBody}
      />
      <Column
        field="accountCount"
        style={{ minWidth: "120px", maxWidth: "150px" }}
        header="# Accounts"
        dataType="numeric"
      />
      <Column
        field="createdAt"
        style={{ minWidth: "120px", maxWidth: "150px" }}
        header="Created"
      />
      <Column
        style={{ maxWidth: "140px" }}
        field="actions"
        header="Actions"
        body={(row) => modelActions(row)}
      />
    </DataTable>
  );
}

export { ModelsTable };
